import {messages} from '@smartmixin/i18n';
import {ThumbsDown, ThumbsUp} from '@phosphor-icons/react';
import {alpha, Box, Button, Chip, Typography, useTheme} from '@mui/material';
import {useState} from 'react';
import ReactGA from 'react-ga4';
import {useIntl} from 'react-intl';
import AppPaper from '../../components/AppPaper';

type ArticleFeedbackProps = {
  articleId: string;
};

// GA reason values (kebab-case per analytics conventions)
const REASONS = [
  {value: 'outdated', message: messages.helpCenter.feedbackReasonOutdated},
  {value: 'unclear', message: messages.helpCenter.feedbackReasonUnclear},
  {value: 'not-answer', message: messages.helpCenter.feedbackReasonNotAnswer},
];

const ArticleFeedback = ({articleId}: ArticleFeedbackProps) => {
  const theme = useTheme();
  const {formatMessage} = useIntl();
  const [feedback, setFeedback] = useState<'helpful' | 'not-helpful' | null>(
    null,
  );
  const [reasonDone, setReasonDone] = useState(false);

  const handleFeedback = (value: 'helpful' | 'not-helpful') => {
    setFeedback(value);
    ReactGA.gtag('event', 'article_feedback', {
      article_id: articleId,
      feedback: value,
    });
  };

  const handleReason = (reason: string) => {
    setReasonDone(true);
    ReactGA.gtag('event', 'article_feedback_reason', {
      article_id: articleId,
      reason,
    });
  };

  // Negative vote: ask what went wrong before thanking (one tap, skippable)
  if (feedback === 'not-helpful' && !reasonDone) {
    return (
      <AppPaper sx={{p: 3, mt: 4, textAlign: 'center'}}>
        <Typography variant="body1" sx={{fontWeight: 500, mb: 2}}>
          {formatMessage(messages.helpCenter.feedbackWhatWentWrong)}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'center',
            flexWrap: 'wrap',
            mb: 1.5,
          }}>
          {REASONS.map((reason) => (
            <Chip
              key={reason.value}
              label={formatMessage(reason.message)}
              onClick={() => handleReason(reason.value)}
              variant="outlined"
              sx={{
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            />
          ))}
        </Box>
        <Button
          size="small"
          onClick={() => setReasonDone(true)}
          sx={{color: 'text.secondary'}}>
          {formatMessage(messages.common.skip)}
        </Button>
      </AppPaper>
    );
  }

  if (feedback) {
    return (
      <AppPaper
        sx={{
          p: 3,
          mt: 4,
          textAlign: 'center',
          backgroundColor: alpha(theme.palette.success.main, 0.05),
          borderColor: alpha(theme.palette.success.main, 0.2),
        }}>
        <Typography
          variant="body1"
          sx={{fontWeight: 500, color: 'success.main'}}>
          {formatMessage(messages.helpCenter.thankYouForFeedback)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}>
          {formatMessage(messages.helpCenter.feedbackHelpsImprove)}
        </Typography>
      </AppPaper>
    );
  }

  return (
    <AppPaper sx={{p: 3, mt: 4, textAlign: 'center'}}>
      <Typography variant="body1" sx={{fontWeight: 500, mb: 2}}>
        {formatMessage(messages.helpCenter.wasArticleHelpful)}
      </Typography>
      <Box sx={{display: 'flex', gap: 2, justifyContent: 'center'}}>
        <Button
          variant="outlined"
          startIcon={<ThumbsUp />}
          onClick={() => handleFeedback('helpful')}
          sx={{
            borderColor: alpha(theme.palette.success.main, 0.5),
            color: 'success.main',
            '&:hover': {
              borderColor: theme.palette.success.main,
              backgroundColor: alpha(theme.palette.success.main, 0.05),
            },
          }}>
          {formatMessage(messages.common.yes)}
        </Button>
        <Button
          variant="outlined"
          startIcon={<ThumbsDown />}
          onClick={() => handleFeedback('not-helpful')}
          sx={{
            borderColor: alpha(theme.palette.error.main, 0.5),
            color: 'error.main',
            '&:hover': {
              borderColor: theme.palette.error.main,
              backgroundColor: alpha(theme.palette.error.main, 0.05),
            },
          }}>
          {formatMessage(messages.common.no)}
        </Button>
      </Box>
    </AppPaper>
  );
};

export default ArticleFeedback;
